'use client';

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  Calculator,
  Scale,
  Calendar,
  DollarSign,
  Code,
  Globe,
  Shield,
  Users,
  Palette,
  Megaphone,
  Briefcase,
  Zap,
  Star,
  Clock,
  FileText,
  Search,
  Sparkles,
  Lock,
  Smartphone,
} from "lucide-react";

type ToolSummary = {
  name: string;
  slug: string;
  description?: string;
  category?: string;
  usage?: string;
};

const PRIMARY = "#4F46E5";
const SECONDARY = "#7C3AED";
const ACCENT = "#22C55E";

const CATEGORIES = [
  {
    name: "Health",
    icon: Scale,
    href: "/tools?category=health",
    count: 12,
    description: "BMI, calorie, water intake & more.",
  },
  {
    name: "Dev Tools",
    icon: Code,
    href: "/tools?category=developer",
    count: 40,
    description: "Formatters, debuggers, generators.",
  },
  {
    name: "Encryption",
    icon: Shield,
    href: "/tools?category=hash-calculators",
    count: 18,
    description: "Hash, encrypt, sign & verify.",
  },
  {
    name: "Calculators",
    icon: Calculator,
    href: "/tools?category=calculators",
    count: 25,
    description: "EMI, ROI, age, salary & more.",
  },
  {
    name: "Converters",
    icon: Globe,
    href: "/tools?category=conversion",
    count: 28,
    description: "JSON, CSV, XML, YAML, HTML.",
  },
  {
    name: "Text Tools",
    icon: FileText,
    href: "/tools?category=text-tools",
    count: 22,
    description: "Counters, cleaners, formatters.",
  },
];

const POPULAR_TOOLS = [
  {
    title: "JSON Formatter",
    description: "Validate & beautify JSON with error hints.",
    icon: Code,
    href: "/tools/json-formatter",
    usage: "Used by 35k+ developers",
    tag: "Dev Essential",
  },
  {
    title: "BMI Calculator",
    description: "Instant BMI with healthy range insights.",
    icon: Scale,
    href: "/bmi-calculator",
    usage: "Used by 50k+ people",
    tag: "Health",
  },
  {
    title: "EMI Calculator",
    description: "Plan loans with clear EMI breakdown.",
    icon: DollarSign,
    href: "/emi-calculator",
    usage: "Used by 60k+ professionals",
    tag: "Finance",
  },
  {
    title: "Character Counter",
    description: "Track length, words & limits in real time.",
    icon: FileText,
    href: "/tools/character-counter",
    usage: "Used by 28k+ creators",
    tag: "Content",
  },
];

const SEARCH_SUGGESTIONS: ToolSummary[] = [
  {
    name: "BMI Calculator",
    slug: "/bmi-calculator",
    description: "Check your BMI instantly.",
    category: "Health",
  },
  {
    name: "JSON Formatter",
    slug: "/tools/json-formatter",
    description: "Format & validate JSON.",
    category: "Dev Tools",
  },
  {
    name: "Age Calculator",
    slug: "/age-calculator",
    description: "Exact age in years & days.",
    category: "Calculators",
  },
  {
    name: "EMI Calculator",
    slug: "/emi-calculator",
    description: "Loan EMI & interest split.",
    category: "Finance",
  },
  {
    name: "Text Case Converter",
    slug: "/tools/text-case-converter",
    description: "UPPERCASE, camelCase, title case.",
    category: "Text Tools",
  },
  {
    name: "Base64 Encoder / Decoder",
    slug: "/tools/base64",
    description: "Encode & decode Base64.",
    category: "Encryption",
  },
];

const INITIAL_STATS = [
  { label: "Free tools", value: 140, suffix: "+", icon: Zap },
  { label: "Monthly users", value: 25000, suffix: "+", icon: Users },
  { label: "Avg. rating", value: 4.8, suffix: "/5", icon: Star },
  { label: "Countries", value: 120, suffix: "+", icon: Globe },
];

export default function Home() {
  const [searchValue, setSearchValue] = useState("");
  const [recentTools, setRecentTools] = useState<ToolSummary[]>([]);
  const [visibleStats, setVisibleStats] = useState(
    INITIAL_STATS.map((s) => ({ ...s, display: 0 }))
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem("toolvix-recent-tools");
      if (!raw) return;
      const parsed = JSON.parse(raw) as ToolSummary[];
      if (Array.isArray(parsed)) {
        setRecentTools(parsed.slice(0, 4));
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  useEffect(() => {
    const duration = 900;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);

      setVisibleStats(
        INITIAL_STATS.map((s) => ({
          ...s,
          display: s.value * eased,
        }))
      );

      if (t < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, []);

  const filteredSuggestions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return SEARCH_SUGGESTIONS.slice(0, 5);
    return SEARCH_SUGGESTIONS.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [searchValue]);

  return (
    <div className="relative">
      {/* Hero */}
      <section
        id="hero"
        className="relative overflow-hidden border-b border-slate-200/60 bg-gradient-to-br from-[#EEF2FF] via-[#EEF2FF] to-[#F5F3FF] pb-16 pt-14 text-slate-900 dark:border-slate-800/50 dark:from-[#020617] dark:via-[#020617] dark:to-[#020617]"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-72 w-72 rounded-full bg-[#4F46E5]/10 blur-3xl" />
          <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#7C3AED]/10 blur-3xl" />
          <div className="absolute left-1/2 top-32 h-40 w-40 -translate-x-1/2 rounded-3xl bg-white/40 blur-2xl dark:bg-slate-900/30" />
        </div>

        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-10 px-4 text-center sm:px-6 lg:px-8">
          <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/80 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm shadow-slate-200 backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200 dark:shadow-slate-900/60">
            <Sparkles className="h-3.5 w-3.5 text-[#4F46E5]" />
            140+ free tools · No login · Privacy first
          </span>

          <div className="space-y-6">
            <h1 className="text-balance text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl md:text-[48px] md:leading-[1.1] dark:text-slate-50">
              140+ Free Online Tools.{" "}
              <span className="bg-gradient-to-r from-[#4F46E5] via-[#6366F1] to-[#7C3AED] bg-clip-text text-transparent">
                Instant. Private. No Signup.
              </span>
            </h1>
            <p className="mx-auto max-w-2xl text-balance text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
              Everything you need — calculators, converters, developer tools —
              in one fast, privacy-friendly workspace designed for modern teams
              and solo creators.
            </p>
            <p className="mx-auto max-w-2xl text-balance text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              ToolVix is a free tools site: we build and maintain 133+ online tools so you can calculate, convert, format, and generate without sign-up or cost. Use our BMI and EMI calculators, JSON and code formatters, hash generators, SEO and content tools, and more—all in your browser, with no data stored on our servers.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              href="/tools"
              className="inline-flex items-center justify-center rounded-2xl bg-[#4F46E5] px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:bg-[#4338CA] hover:shadow-xl"
            >
              Explore Tools
            </Link>
            <Link
              href="/tools?view=categories"
              className="inline-flex items-center justify-center rounded-2xl border border-slate-300/80 bg-white/70 px-7 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-[#4F46E5]/50 hover:text-[#4F46E5] dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:hover:border-[#6366F1]"
            >
              Browse Categories
            </Link>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 shadow-sm shadow-slate-200 backdrop-blur dark:bg-slate-900/70 dark:shadow-slate-950/80">
              <Lock className="h-3.5 w-3.5 text-emerald-500" />
              <span>Privacy First</span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 shadow-sm shadow-slate-200 backdrop-blur dark:bg-slate-900/70 dark:shadow-slate-950/80">
              <Sparkles className="h-3.5 w-3.5 text-[#22C55E]" />
              <span>100% Free · No Login</span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 shadow-sm shadow-slate-200 backdrop-blur dark:bg-slate-900/70 dark:shadow-slate-950/80">
              <Smartphone className="h-3.5 w-3.5 text-[#7C3AED]" />
              <span>Works on Any Device</span>
            </div>
          </div>
        </div>
      </section>

      {/* Powerful search */}
      <section className="border-b border-slate-200/60 bg-white/90 py-10 sm:py-12 backdrop-blur-sm dark:border-slate-800/60 dark:bg-slate-950/80">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-5 text-center">
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#4F46E5] dark:text-[#A5B4FC]">
              Powerful search
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Find the right tool in seconds with live suggestions.
            </p>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -inset-x-10 -top-6 h-10 bg-gradient-to-b from-[#EEF2FF]/70 to-transparent dark:from-slate-900/60" />
            <div className="relative rounded-3xl border border-slate-200 bg-slate-50/80 p-2 shadow-sm shadow-slate-200 backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/70 dark:shadow-slate-950">
              <div className="flex items-center gap-3 rounded-2xl bg-white/90 px-4 py-2.5 shadow-sm shadow-slate-200 dark:bg-slate-950/80 dark:shadow-slate-950">
                <Search className="h-4 w-4 flex-none text-slate-400" />
                <input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search for tools like BMI Calculator"
                  className="h-8 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                />
                <span className="hidden items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-300 sm:inline-flex">
                  <kbd className="rounded border border-slate-300 bg-white px-1 text-[9px] font-semibold text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300">
                    /
                  </kbd>
                  Quick search
                </span>
              </div>

              <div className="mt-2 rounded-2xl border border-dashed border-slate-200 bg-white/90 px-3 py-2 text-left text-xs text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-950/90 dark:text-slate-400">
                <p className="mb-1 font-medium text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Suggestions
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {filteredSuggestions.map((tool) => (
                    <Link
                      key={tool.slug}
                      href={tool.slug}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                      <span>{tool.name}</span>
                      {tool.category && (
                        <span className="text-[10px] text-slate-400">
                          · {tool.category}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + recent */}
      <section className="border-b border-slate-200/60 bg-[#F9FAFB] py-12 dark:border-slate-800/60 dark:bg-slate-950">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 sm:px-6 lg:flex-row lg:px-8">
          <div className="flex-1 space-y-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              Trusted, fast & privacy-first.
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {visibleStats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-900 dark:shadow-none"
                  >
                    <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-[#4F46E5] dark:bg-slate-800 dark:text-[#A5B4FC]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                      {stat.label === "Avg. rating"
                        ? stat.display.toFixed(1)
                        : Math.round(stat.display).toLocaleString()}
                      {stat.suffix}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {recentTools.length > 0 && (
            <div className="flex-1 rounded-2xl border border-dashed border-slate-300 bg-white/90 p-4 shadow-sm shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-900/90 dark:shadow-none">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Recent tools
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Pick up where you left off.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {recentTools.map((tool) => (
                  <Link
                    key={tool.slug}
                    href={tool.slug}
                    className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-[#4F46E5] dark:bg-slate-800 dark:text-[#A5B4FC]">
                        <Clock className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{tool.name}</span>
                        {tool.description && (
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">
                            {tool.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                      Open →
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      <section
        id="categories"
        className="border-b border-slate-200/60 bg-white py-14 dark:border-slate-800/60 dark:bg-slate-950"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                Categories
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Health, developer tools, encryption, calculators, converters &
                more.
              </p>
            </div>
            <Link
              href="/tools?view=categories"
              className="inline-flex items-center text-sm font-medium text-[#4F46E5] hover:text-[#4338CA] dark:text-[#A5B4FC]"
            >
              View all categories →
            </Link>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.name}
                  href={category.href}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/60 p-4 shadow-sm shadow-slate-200/70 transition hover:-translate-y-1 hover:border-[#4F46E5]/40 hover:shadow-lg dark:border-slate-800 dark:from-slate-900 dark:to-slate-950 dark:shadow-none"
                >
                  <div className="absolute right-3 top-3 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                    {category.count} tools
                  </div>
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white shadow-md shadow-indigo-500/30">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                        {category.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Curated premium utilities.
                      </p>
                    </div>
                  </div>
                  <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                    {category.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular tools */}
      <section
        id="popular"
        className="border-b border-slate-200/60 bg-[#F9FAFB] py-14 dark:border-slate-800/60 dark:bg-slate-950"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                Popular right now
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Tools people come back to every single day.
              </p>
            </div>
            <Link
              href="/tools?sort=popular"
              className="inline-flex items-center text-sm font-medium text-[#4F46E5] hover:text-[#4338CA] dark:text-[#A5B4FC]"
            >
              See all popular tools →
            </Link>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {POPULAR_TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.title}
                  href={tool.href}
                  className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm shadow-slate-200/70 transition hover:-translate-y-1 hover:border-[#4F46E5]/40 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
                >
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white shadow-sm shadow-indigo-500/40">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        Trending
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] dark:text-slate-50">
                      {tool.title}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                      {tool.description}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span>{tool.usage}</span>
                    <span className="text-[11px] font-semibold text-[#4F46E5] group-hover:text-[#4338CA] dark:text-[#A5B4FC]">
                      Use tool →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why ToolVix */}
      <section className="border-b border-slate-200/60 bg-white py-14 dark:border-slate-800/60 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              Why teams choose ToolVix
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Built for developers, analysts, founders and students who care
              about trust, speed and focus.
            </p>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-none">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-[#4F46E5] dark:bg-slate-800 dark:text-[#A5B4FC]">
                <Zap className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                Instant results
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                Optimized for single-purpose workflows. Open a tool, paste your
                data, get an answer — no onboarding, no friction.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-none">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <Lock className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                Privacy focused
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                Most tools run entirely in your browser. No accounts, no
                tracking pixels, no surprise data retention.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-none">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                <Smartphone className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                Works on any device
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                A responsive, app-like experience on desktop, tablet and mobile
                with an always-available bottom nav.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / SEO intro */}
      <section className="bg-[#F9FAFB] py-14 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)]">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                Built to rank — and retain.
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                Every Toolvix page is structured for search — clear titles,
                descriptive copy, FAQs and fast Core Web Vitals. Users get
                answers, search engines get clarity.
              </p>
              <ul className="mt-4 space-y-2 text-xs text-slate-600 dark:text-slate-400">
                <li>· SEO-friendly headings & meta descriptions.</li>
                <li>· Helpful FAQs and usage examples on tool pages.</li>
                <li>· Clean layout optimized for AdSense performance.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
              <h3 className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-50">
                Frequently asked questions
              </h3>
              <div className="space-y-4 text-xs text-slate-600 dark:text-slate-400">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-50">
                    Is Toolvix really free?
                  </p>
                  <p className="mt-1">
                    Yes. All 140+ tools are free to use with no trials, paywalls
                    or credit cards required.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-50">
                    Do I need an account?
                  </p>
                  <p className="mt-1">
                    No login, no signup. Open a tool and start using it
                    immediately.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-50">
                    Is my data stored?
                  </p>
                  <p className="mt-1">
                    For most tools, your data never leaves your browser. For
                    server tools, we discard inputs after processing.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-50">
                    Can I use Toolvix for work?
                  </p>
                  <p className="mt-1">
                    Absolutely. Many teams use Toolvix daily for QA, operations,
                    marketing and development.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
